import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import styles, {C} from "../styles";
import Modal from "./Modal";

const FeedbackContext = createContext(null);

export function FeedbackProvider({children}) {
    const [toast, setToast] = useState(null);
    const [confirmState, setConfirmState] = useState(null);
    const resolveRef = useRef(null);
    const [promptState, setPromptState] = useState(null);
    const [promptValue, setPromptValue] = useState("");
    const promptResolveRef = useRef(null);
    const promptInputRef = useRef(null);

    const notify = useCallback((message, type = "success") => {
        setToast({message, type});
    }, []);

    const closeToast = useCallback(() => setToast(null), []);

    const confirm = useCallback((message, {danger = false} = {}) => {
        setToast(null);
        return new Promise(resolve => {
            resolveRef.current = resolve;
            setConfirmState({message, danger});
        });
    }, []);

    const prompt = useCallback((message, {defaultValue = "", placeholder = ""} = {}) => {
        setToast(null);
        return new Promise(resolve => {
            promptResolveRef.current = resolve;
            setPromptValue(defaultValue);
            setPromptState({message, placeholder});
        });
    }, []);

    const contextValue = useMemo(() => ({notify, confirm, prompt}), [notify, confirm, prompt]);

    function resolveConfirm(result) {
        setConfirmState(null);
        if (resolveRef.current) {
            resolveRef.current(result);
            resolveRef.current = null;
        }
    }

    function resolvePrompt(result) {
        setPromptState(null);
        if (promptResolveRef.current) {
            promptResolveRef.current(result);
            promptResolveRef.current = null;
        }
    }

    return (
        <FeedbackContext.Provider value={contextValue}>
            {children}
            {toast && (
                <div
                    role="alert"
                    style={{
                        position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
                        zIndex: 2000, maxWidth: 480, width: "calc(100% - 24px)",
                        backgroundColor: toast.type === "error" ? C.dangerLight : C.primaryLight,
                        color: toast.type === "error" ? C.danger : C.primaryDark,
                        border: `1px solid ${toast.type === "error" ? C.danger : C.primary}55`,
                        borderRadius: 10, padding: "12px 16px",
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                    }}
                >
                    <span>{toast.message}</span>
                    <button
                        onClick={closeToast}
                        style={{background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit", lineHeight: 1}}
                    >
                        ✕
                    </button>
                </div>
            )}
            {confirmState && (
                <Modal title="Confirmer" onClose={() => resolveConfirm(false)}>
                    <p style={{margin: "0 0 16px", fontSize: 14, lineHeight: 1.5, color: C.text}}>
                        {confirmState.message}
                    </p>
                    <div style={{display: "flex", justifyContent: "flex-end", gap: 8}}>
                        <button onClick={() => resolveConfirm(false)} style={styles.button}>
                            Annuler
                        </button>
                        <button
                            onClick={() => resolveConfirm(true)}
                            style={confirmState.danger ? styles.dangerButton : styles.primaryButton}
                        >
                            Confirmer
                        </button>
                    </div>
                </Modal>
            )}
            {promptState && (
                <Modal title="Saisie" onClose={() => resolvePrompt(null)} initialFocusRef={promptInputRef}>
                    <form onSubmit={e => { e.preventDefault(); resolvePrompt(promptValue); }}>
                        <p style={{margin: "0 0 12px", fontSize: 14, lineHeight: 1.5, color: C.text}}>
                            {promptState.message}
                        </p>
                        <input
                            ref={promptInputRef}
                            value={promptValue}
                            onChange={e => setPromptValue(e.target.value)}
                            placeholder={promptState.placeholder}
                            style={{...styles.input, width: "100%", boxSizing: "border-box", marginBottom: 16}}
                        />
                        <div style={{display: "flex", justifyContent: "flex-end", gap: 8}}>
                            <button type="button" onClick={() => resolvePrompt(null)} style={styles.button}>
                                Annuler
                            </button>
                            <button type="submit" style={styles.primaryButton}>
                                Valider
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </FeedbackContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(FeedbackContext);
    if (!ctx) throw new Error("useToast doit être utilisé à l'intérieur de FeedbackProvider");
    return {notify: ctx.notify};
}

export function useConfirm() {
    const ctx = useContext(FeedbackContext);
    if (!ctx) throw new Error("useConfirm doit être utilisé à l'intérieur de FeedbackProvider");
    return ctx.confirm;
}

export function usePrompt() {
    const ctx = useContext(FeedbackContext);
    if (!ctx) throw new Error("usePrompt doit être utilisé à l'intérieur de FeedbackProvider");
    return ctx.prompt;
}
